import * as React from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { Alert, AlertDescription, AlertTitle } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { ViewContext } from "components/lib";
import { DOCUMENT_LABELS, getRequiredDocumentTypes } from "../utils/schema";

export function Documents({ user }) {
  const location = useLocation();
  const context = React.useContext(ViewContext);
  const { setValue, getValues, watch, register } = useFormContext();

  const carriers = watch("step1.carrier") || [];
  const vehicles = watch("step6.vehicles") || [];
  const requiredTypes = React.useMemo(
    () => getRequiredDocumentTypes({ step1: { carrier: carriers }, step6: { vehicles } }),
    [carriers, vehicles]
  );
  const requiredUploads = React.useMemo(
    () =>
      requiredTypes.map((type) => ({
        id: type,
        title: DOCUMENT_LABELS[type] ?? type,
        required: true,
        files: [],
      })),
    [requiredTypes]
  );

  const [uploads, setUploads] = React.useState(requiredUploads);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState("");

  const companyId = location?.state?.data?.company_id;
  const quoteId = location?.state?.data?.quote_id;
  const documentOwnerId = companyId || quoteId;
  const savedDocs = watch("step10.documents") || [];

  React.useEffect(() => {
    register("step10.documents");
  }, [register]);

  React.useEffect(() => {
    setUploads((prev) =>
      requiredUploads.map((upload) => ({
        ...upload,
        files: prev.find((item) => item.id === upload.id)?.files ?? [],
      }))
    );
  }, [requiredUploads]);

  const clearDocsForType = React.useCallback(
    (type) => {
      const remaining = (getValues("step10.documents") || []).filter((doc) => doc.type !== type);
      setValue("step10.documents", remaining, { shouldDirty: true, shouldValidate: true });
    },
    [getValues, setValue]
  );

  const resetUploads = React.useCallback(() => {
    setUploads(requiredUploads.map((u) => ({ ...u, files: [] })));
    setError("");
  }, [requiredUploads]);

  const handleFileUpload = (uploadId, files) => {
    setUploads((prev) =>
      prev.map((upload) =>
        upload.id === uploadId ? { ...upload, files: Array.from(files ?? []) } : upload
      )
    );
    setError("");
  };

  const uploadDocuments = async () => {
    if (!documentOwnerId) {
      const message = "Save the quote first so we can attach your documents.";
      setError(message);
      context.notification?.show?.(message, "warning", true);
      return;
    }

    const missingRequired = uploads.filter(
      (u) =>
        u.required &&
        u.files.length === 0 &&
        !savedDocs.some(
          (doc) => doc.type === u.id && (doc.url || doc.s3_link || "").trim().length
        )
    );
    if (missingRequired.length) {
      setError("Please upload all required documents before continuing.");
      return;
    }

    setUploading(true);
    try {
      const currentDocs = getValues("step10.documents") || [];
      const nextDocs = [...currentDocs];

      for (const upload of uploads) {
        for (const file of upload.files) {
          const payload = {
            name: file.name,
            type: upload.id,
            uploaded_by: user?.data?.first_name || user?.data?.name || "Unknown",
            company_id: documentOwnerId,
          };

          const documentResponse = await axios.post("/api/document", payload);
          const documentId = documentResponse.data?.data?.id ?? documentResponse.data?.id;

          if (!documentId) {
            throw new Error("Document could not be created");
          }

          const formData = new FormData();
          formData.append("file", file);
          const fileUploadResponse = await axios.post(`/api/document/${documentId}/file`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          const resolvedUrl =
            fileUploadResponse?.data?.data?.s3_link ||
            fileUploadResponse?.data?.data?.url ||
            fileUploadResponse?.data?.url ||
            `/api/document/${documentId}/download`;

          nextDocs.push({
            type: upload.id,
            url: resolvedUrl,
            name: file.name,
            id: documentId,
            s3_link: fileUploadResponse?.data?.data?.s3_link,
          });
        }
      }

      setValue("step10.documents", nextDocs, { shouldDirty: true, shouldValidate: true });
      context.notification?.show?.("Documents uploaded successfully.", "success", true);
      resetUploads();
    } catch (err) {
      console.error("Failed to upload documents", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while uploading documents.";
      setError(message);
      context.notification?.show?.(message, "error", true);
    } finally {
      setUploading(false);
    }
  };

  if (!requiredUploads.length) return null;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-gray-900">Required Documents</h2>
        <p className="text-sm text-gray-600">
          {requiredTypes.includes("ifta")
            ? "Upload the last 4 quarters of IFTAs, last 3 years of loss runs, and MVRs for each driver."
            : "Upload the required documents so we can attach them to this quote."}
        </p>
      </div>

      {!documentOwnerId && (
        <Alert variant="destructive">
          <AlertTitle>Save required</AlertTitle>
          <AlertDescription>
            Save the quote first so we can attach your documents to it.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {uploads.map((upload) => (
          <div
            key={upload.id}
            className="border-2 border-dashed border-gray-200 rounded-lg bg-gray-50"
          >
            <div className="flex items-center justify-between px-4 pt-4">
              <div className="text-sm font-semibold text-gray-900">
                {upload.title} {upload.required && <span className="text-red-600">*</span>}
              </div>
              {upload.files.length > 0 && (
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline"
                  onClick={() => {
                    handleFileUpload(upload.id, []);
                    clearDocsForType(upload.id);
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            <label
              htmlFor={`doc-upload-${upload.id}`}
              className="flex flex-col items-center justify-center gap-2 px-4 py-6 cursor-pointer hover:bg-white"
            >
              <input
                id={`doc-upload-${upload.id}`}
                type="file"
                className="hidden"
                multiple
                onChange={(e) => handleFileUpload(upload.id, e.target.files)}
              />
              <div className="text-2xl">📁</div>
              <div className="text-sm text-gray-700 text-center">
                {upload.files.length
                  ? `${upload.files.length} file(s) selected`
                  : `Drag & drop or click to upload ${upload.title}`}
              </div>
              <div className="text-xs text-gray-500 text-center">
                PDF, DOC, DOCX, JPG, PNG • Max 10MB per file
              </div>
              {upload.files.length > 0 && (
                <ul className="text-xs text-gray-600 text-center">
                  {upload.files.map((file) => (
                    <li key={file.name}>{file.name}</li>
                  ))}
                </ul>
              )}
            </label>
          </div>
        ))}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {savedDocs.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">Saved document links</h3>
          <ul className="space-y-1 text-sm">
            {savedDocs.map((doc, idx) => (
              <li key={`${doc.type}-${idx}`} className="flex items-center justify-between gap-2">
                <span className="text-gray-700">
                  {doc.name || DOCUMENT_LABELS[doc.type] || doc.type}
                </span>
                {doc.s3_link || doc.url ? (
                  <button
                    type="button"
                    className="text-blue-600 underline break-all"
                    onClick={async () => {
                      const href = doc.s3_link || doc.url;
                      if (!href) return;
                      if (href.startsWith("/api/document/")) {
                        try {
                          const resp = await axios.get(href);
                          const direct = resp?.data?.link || href;
                          window.open(direct, "_blank", "noopener,noreferrer");
                          return;
                        } catch (err) {
                          console.error("Failed to resolve document link", err);
                        }
                      }
                      window.open(href, "_blank", "noopener,noreferrer");
                    }}
                  >
                    Download
                  </button>
                ) : (
                  <span className="text-xs text-gray-500">No link available</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={resetUploads}
          disabled={uploading}
        >
          Reset
        </Button>
        <Button
          type="button"
          onClick={uploadDocuments}
          disabled={uploading || !documentOwnerId}
        >
          {uploading ? "Uploading..." : "Upload Documents"}
        </Button>
      </div>
    </div>
  );
}
